import { useCallback, useState } from 'react';
import { PlatformModal } from './PlatformModal.js';

/**
 * Type-to-confirm operator modal (D-5 dunning polish). For consequential manual
 * subscription transitions (→ past_due, → read_only) the operator must type the
 * tenant's legal name exactly AND give a reason before the confirm button
 * enables — a safeguard against an accidental click past_due-ing a healthy
 * production tenant (e.g. MokoAfrika), which would trigger dunning emails on the
 * next cron tick.
 *
 * Composes PlatformModal (Indigo-Deep shell). Controlled inputs (useState) so
 * the confirm-enable gate is deterministic. The parent owns the mutation,
 * success-close, and query invalidation — this modal only gates the action and
 * returns the reason. The reason mirrors the C-2 server Zod (1–500 chars).
 */
export function PlatformConfirmTransitionModal({
  open,
  onClose,
  title,
  warning,
  expectedConfirmText,
  confirmLabel,
  isSubmitting = false,
  errorMessage,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  /** The consequence text, named explicitly. */
  warning: string;
  /** The tenant legal name the operator must type to enable confirm. */
  expectedConfirmText: string;
  confirmLabel: string;
  isSubmitting?: boolean;
  errorMessage?: string;
  onConfirm: (reason: string) => Promise<void> | void;
}): JSX.Element | null {
  const [confirmText, setConfirmText] = useState('');
  const [reason, setReason] = useState('');

  // Memoised so its identity is stable across keystroke re-renders. PlatformModal's
  // focus effect depends on the onClose it receives; an unstable handleClose would
  // re-fire that effect on every keystroke, stealing focus back to the dialog and
  // dropping characters from the (controlled) inputs.
  const handleClose = useCallback((): void => {
    setConfirmText('');
    setReason('');
    onClose();
  }, [onClose]);

  if (!open) return null;

  // Compare TRIMMED. The point of type-to-confirm is "prove you looked at which
  // tenant this is" — not "reproduce invisible characters". A tenant legal name
  // carrying a trailing space (real: `Apps and Mobile Nigiera Limited ` in prod)
  // makes the exact match unsatisfiable by typing what you SEE: the operator has
  // no way to know a space is missing, and the button just stays dead. Trimming
  // only forgives leading/trailing whitespace — every visible character, the
  // spelling, and the case must still match exactly, so the guard's real work
  // (you must read the actual record, typos and all) is untouched.
  const nameMatches = confirmText.trim() === expectedConfirmText.trim();
  const trimmedReason = reason.trim();
  const reasonOk = trimmedReason.length > 0 && trimmedReason.length <= 500;
  const canConfirm = nameMatches && reasonOk && !isSubmitting;

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!canConfirm) return;
    void onConfirm(reason);
  };

  return (
    <PlatformModal
      open={open}
      onClose={handleClose}
      title={title}
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="platform-confirm-transition-form"
            disabled={!canConfirm}
            className="px-4 py-2 rounded-md font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
          >
            {isSubmitting ? 'Submitting…' : confirmLabel}
          </button>
        </>
      }
    >
      <form id="platform-confirm-transition-form" onSubmit={handleSubmit}>
        <div className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
          {warning}
        </div>

        <label htmlFor="platform-confirm-text" className="block text-sm font-medium text-gray-700 mb-1">
          Type <span className="font-semibold">{expectedConfirmText}</span> to confirm
        </label>
        <input
          id="platform-confirm-text"
          type="text"
          autoComplete="off"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          disabled={isSubmitting}
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-primary"
          placeholder={expectedConfirmText}
        />

        <label htmlFor="platform-confirm-reason" className="mt-3 block text-sm font-medium text-gray-700 mb-1">
          Reason
        </label>
        <textarea
          id="platform-confirm-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={isSubmitting}
          rows={3}
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-primary"
          placeholder="Written record for the audit trail…"
        />

        {errorMessage ? (
          <p className="mt-2 text-sm text-red-600" role="alert">
            {errorMessage}
          </p>
        ) : null}
      </form>
    </PlatformModal>
  );
}
